import React, { useEffect, useCallback } from "react";

import { useFormik } from "formik";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

// Services

import {
  consultaListasInternas,
  consultaListasInternacionales,
  insertarClienteEnListaParaRevision,
  clearConsultaListasInternas,
  clearConsultaAlertaSafeWatch, 
  clearInsertarClienteEnListaParaRevision 
} from "../../../store/lists/lists.actions";
import { consultarClientesMis, clearConsultarClientesMisStore } from '../../../store/clients/clients.actions';
import { cleanUpClienteDataParcial } from '../../../store/forms/forms.actions';

// Hooks

import InsertClientpartialData from '../../../hooks/InsertClientpartialData';

// Components

import TextInput from "../../UI/FormInput/TextInput";
import FormInputGroup from "../../UI/FormInputGroup/FormInputGroup";
import DropDownInput from "../../UI/FormInput/DropDownInput";
import MaskInput from "../../UI/FormInput/MaskInput";
import Button from "../../UI/Button/Button";

// Validation

import { validationSchema } from "./ValidationSchema";

// Utilities

import { formatDropDownOptions, setUpConsultClientData } from "../../../utilities/helper-functions";

// Constants

import { IDENTIFICATION_MASK_FORMAT, PASSPORT_MASK_FORMAT, CLIENT_LISTS } from "../../../config/constants";
import Alert from "../../UI/Alert/Alert";

export default function EditNameAndDocumentForm({form, formInitialValues, state, setShowEditNameModal}) {
  const { insertPartialDataIsLoading, insertPartialDataError, insertPartialData, handleInsertPartialData } = InsertClientpartialData();
  // State
  const dropDownOptions = useSelector((state) => state.options.dropDownOptions);
  const consultClientsMis = useSelector((state) => state.clients.consultClientsMis);
  const consultInternalLists = useSelector((state) => state.lists.consultInternalLists);
  const consultInternationalLists = useSelector((state) => state.lists.consultInternationalLists);
  const insertClientOnListForReview = useSelector((state) => state.lists.insertClientOnListForReview);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Setup form with formik

  const formik = useFormik({
    initialValues: {
      cliente: {
        cedulaRif: form?.values?.cliente?.cedulaRif,
        nombre: form?.values?.cliente?.nombre,
        pasaporte: form?.values?.cliente?.pasaporte,
        primerApellido: form?.values?.cliente?.primerApellido,
        segundoApellido: form?.values?.cliente?.segundoApellido,
        tipoDocumento: form?.values?.cliente?.tipoDocumento
      },
    },
    validationSchema: validationSchema,
    onSubmit: (values, actions) => {
      handelCheckIfUserExists(values, actions);
    },
  });

  useEffect(() => {
    dispatch(clearConsultarClientesMisStore());
    dispatch(clearConsultaListasInternas());
    dispatch(clearInsertarClienteEnListaParaRevision());
    dispatch(cleanUpClienteDataParcial());
  }, [dispatch]);


  // Handle Check if the user already exists

  const handelCheckIfUserExists = (values, actions) => {
    const documentId = values?.cliente?.tipoDocumento !== 'P' ? values?.cliente?.tipoDocumento + values?.cliente?.cedulaRif : values?.cliente?.pasaporte;
    const data = setUpConsultClientData(documentId);

    if (data) {
      dispatch(consultarClientesMis(data));
    }

  }


  // Handle Consult Internal Lists

  const handleConsultInternalLists = useCallback((values) => {
    const data = {
      DNI: values?.cliente?.tipoDocumento !== 'P' ? values?.cliente?.tipoDocumento + values?.cliente?.cedulaRif : values?.cliente?.pasaporte,
    };
    dispatch(consultaListasInternas(data));
  }, []);

  useEffect(() => {
    if (consultClientsMis?.data?.exists === false) {
      handleConsultInternalLists(formik?.values);
    }
  }, [consultClientsMis?.data, handleConsultInternalLists]);

  useEffect(() => {
    if (consultInternalLists?.error?.codigoError === "1") {
      handleInsertarClienteEnListaParaRevision();
    } else if (consultInternalLists?.data?.codigoError === "0") {
      handleConsultInternationalLists(formik?.values);
    }
  }, [consultInternalLists, navigate]);


  // Handle Consult International Lists

  const handleConsultInternationalLists = useCallback((values) => {
    const data = {
      nombre: values?.cliente?.nombre + " " + values?.cliente?.primerApellido + " " + values?.cliente?.segundoApellido,
      identifiacion: values?.cliente?.tipoDocumento !== 'P' ? values?.cliente?.tipoDocumento + values?.cliente?.cedulaRif : values?.cliente?.pasaporte,
    };
    dispatch(consultaListasInternacionales(data));
  }, []);


  // Handle Insert Client Partial Data

  const handleInsertClientPartialData = useCallback((values) => {
    const data = {
      ...state?.userPartialData,
      documentId: values?.cliente?.tipoDocumento !== 'P' ? values?.cliente?.tipoDocumento + values?.cliente?.cedulaRif : values?.cliente?.pasaporte,
      primerApellido: values?.cliente?.primerApellido,
      segundoApellido: values?.cliente?.segundoApellido,
      tipoDocumento: values?.cliente?.tipoDocumento,
      cedulaRif: values?.cliente?.tipoDocumento !== 'P' ? values?.cliente?.cedulaRif : '',
      pasaporte: values?.cliente?.tipoDocumento === 'P' ? values?.cliente?.pasaporte : '',
      data: {
        ...form?.values,
        recaudos: { ...formInitialValues?.recaudos }
      },
    }
    handleInsertPartialData(data);
  }, []);

  useEffect(() => {
    if (consultInternationalLists?.error?.codigoError === "1") {
      handleInsertarClienteEnListaParaRevision();
    } else if (consultInternationalLists?.data?.codigoError === "0") {
      handleInsertClientPartialData(formik?.values);
    }
  }, [consultInternationalLists]);


  // Handle Insert Client On List For Review

  const handleInsertarClienteEnListaParaRevision = async () => {
    const clientEmail = localStorage.getItem("clientEmail");
    const data = {
      full_name: formik?.values?.cliente?.nombre + " " + formik?.values?.cliente?.primerApellido + " " + formik?.values?.cliente?.segundoApellido,
      email: clientEmail,
      document: formik?.values?.cliente?.tipoDocumento !== 'P' ? formik?.values?.cliente?.tipoDocumento + formik?.values?.cliente?.cedulaRif : formik?.values?.cliente?.pasaporte,
      font: consultInternationalLists?.error?.codigoError === "1" ? CLIENT_LISTS.internacional : CLIENT_LISTS.interna,
    };
    dispatch(insertarClienteEnListaParaRevision(data));
  };

  useEffect(() => {
    if (insertClientOnListForReview?.data) {
      handleInsertClientPartialData(formik?.values);
    }
  }, [insertClientOnListForReview?.data]);


  useEffect(() => {
    if (insertPartialData && (consultInternalLists?.error?.codigoError === "1" || consultInternationalLists?.error?.codigoError === "1")) {

      navigate('/solicitud', { state: { userPartialData: { is_blocked: true } }});

    } else if (insertPartialData && consultInternalLists?.data?.codigoError === "0" && consultInternationalLists?.data?.codigoError === "0") {

      form.setFieldValue('cliente.cedulaRif', formik?.values?.cliente?.cedulaRif);
      form.setFieldValue('cliente.nombre', formik?.values?.cliente?.nombre);
      form.setFieldValue('cliente.pasaporte', formik?.values?.cliente?.pasaporte);
      form.setFieldValue('cliente.primerApellido', formik?.values?.cliente?.primerApellido);
      form.setFieldValue('cliente.segundoApellido', formik?.values?.cliente?.segundoApellido);
      form.setFieldValue('cliente.tipoDocumento', formik?.values?.cliente?.tipoDocumento);

      dispatch(clearConsultarClientesMisStore());
      dispatch(clearConsultaListasInternas());
      dispatch(clearConsultaAlertaSafeWatch());
      dispatch(clearInsertarClienteEnListaParaRevision());
      dispatch(cleanUpClienteDataParcial());

      setShowEditNameModal(false);

    }
  }, [insertPartialData]);

  return (
    <form onSubmit={formik.handleSubmit}>

      {/* Form Error Message */}

      {insertPartialDataError && <p className="form-error-message">{insertPartialDataError}</p>}

      {consultClientsMis?.data?.exists &&
      <Alert text={
        <span>
          Estimado usuario, ya usted posee una cuenta con su cédula. Por favor proceda a hacer login en el siguiente link: <Link to={'/'}><strong>Iniciar sesión</strong></Link>.
        </span>
      } />}

      <FormInputGroup>
        <TextInput
            formik={formik}
            fieldName={'cliente.nombre'}
            label={'Nombres'}
            maxLength={42}
            isRequired={true}
            isAlphabetic
        />

        <TextInput
            formik={formik}
            fieldName={'cliente.primerApellido'}
            label={'Primer Apellido'}
            maxLength={20}
            isRequired={true}
            isAlphabetic
        />

        <TextInput
            formik={formik}
            fieldName={'cliente.segundoApellido'}
            label={'Segundo Apellido'}
            maxLength={20}
            isAlphabetic
        />

        <DropDownInput
            formik={formik}
            fieldName={'cliente.tipoDocumento'}
            label={'Tipo de Identificación'}
            options={formatDropDownOptions(dropDownOptions?.data?.identificationTypes)}
            isRequired={true}
        />

        {(formik.values.cliente?.tipoDocumento && formik.values.cliente?.tipoDocumento !== 'P') &&
        <MaskInput
            formik={formik}
            fieldName={'cliente.cedulaRif'}
            label={'Número de Identificación'}
            mask={IDENTIFICATION_MASK_FORMAT}
            isRequired={true}
        />}

        {formik.values.cliente?.tipoDocumento === 'P' &&
        <MaskInput
            formik={formik}
            fieldName={'cliente.pasaporte'}
            label={'Número de Pasaporte'}
            mask={PASSPORT_MASK_FORMAT}
            isRequired={true}
        />}
      </FormInputGroup>

      <div className="form-actions">

        <Button
          type="submit"
          action={formik.handleSubmit}
          label={"Continuar"}
          disabled={!(formik.isValid && formik.dirty)}
          isLoading={
            consultClientsMis?.isLoading ||
            consultInternationalLists?.isLoading ||
            consultInternalLists?.isLoading ||
            insertClientOnListForReview?.isLoading ||
            insertPartialDataIsLoading
          }
          additionalStyles={{ width: "100%", margin: 0 }}
        />

        <Button
          action={() => {
            dispatch(clearConsultarClientesMisStore());
            dispatch(clearConsultaListasInternas());
            dispatch(clearConsultaAlertaSafeWatch());
            dispatch(clearInsertarClienteEnListaParaRevision());
            dispatch(cleanUpClienteDataParcial());
            setShowEditNameModal(false)
          }}
          label={"Cancelar"}
          isLoading={
            consultClientsMis?.isLoading ||
            consultInternationalLists?.isLoading ||
            consultInternalLists?.isLoading ||
            insertClientOnListForReview?.isLoading ||
            insertPartialDataIsLoading
          }
          secondaryStyle={true}
          additionalStyles={{ width: "100%", margin: 0, marginTop: 20 }}
        />

      </div>
    </form>
  );
}
